<script setup>
import { pageTransition } from '~/helpers/animations/animations';

definePageMeta({
  pageTransition: pageTransition,
});

const { locale, t } = useI18n();

const localePath = useLocalePath();

const route = useRoute();
const slug = route.name;

const pageDataQuery = gql`
  query GetPageData($routeId: String) {
    page(route_id: $routeId) {
      seo_meta_title
      seo_meta_description
      seo_meta_keywords
      children {
        id
        name
        image
        route_id
        slug: seo_url_slug {
          cs
          en
        }
        route_id
      }
    }
    buildings(filterVisibleInFilters: true) {
      id
      name
      address
      openingHours {
        from
        to
        day_in_week
      }
      slug: seo_url_slug {
        cs
        en
      }
      image_list
    }
  }
`;
const { data } = await useAsyncQuery(pageDataQuery, {
  routeId: slug,
});

const buildings = computed(() => data.value?.buildings);
const page = computed(() => data.value?.page);

const breadcrumbs = ref([
  {
    title: t('pages.about.index.breadcrumbs-title'),
    route: 'about',
  },
  { title: t('pages.buildings.breadcrumbs-title') },
]);

useHeadData({
  title: page.value?.seo_meta_title || page.value?.name || t('pages.buildings.breadcrumbs-title'),
  description: page.value?.seo_meta_description || null,
  keywords: page.value?.seo_meta_keywords || null,
  image: page.value?.image || page.value?.cropped_detail_image || null,
  url: route.fullPath,
});

const createLink = (child) => {
  let linkObject;
  if (child?.route_id) {
    linkObject = useNoTemplating(child.route_id);
  } else {
    linkObject = {
      name: 'about-page-slug',
      params: { slug: child.slug[locale.value] },
    };
  }
  return linkObject;
};
</script>

<template>
  <main class="main">
    <Breadcrumbs :items="breadcrumbs" />
    <div class="buildings">
      <h1 class="heading">{{ t('pages.buildings.title') }}</h1>
      <div v-if="buildings && buildings.length" class="buildings__list mt-4">
        <div class="pages-container">
          <div v-for="building in buildings" :key="building.id" data-heading-hover>
            <nuxt-link
              :to="
                localePath({
                  name: 'about-buildings-slug',
                  params: { slug: building.slug[locale] },
                })
              "
              class="building"
            >
              <div class="building__image">
                <img :alt="building.name" :src="useGetImageUrl(building.image_list, 250)" class="building__image-elem" />
              </div>
              <div class="building__details">
                <div class="building__name">
                  <h3 class="text text--2">
                    <strong>{{ building.name }}</strong>
                  </h3>
                </div>

                <div class="building__description">{{ building.address }}</div>
                <div class="building__hours">
                  <div class="building__hours-text">
                    <p class="text text--2" v-html="useOpeningHours(building.openingHours)" />
                  </div>
                </div>
                <!-- <div class="building__hours">
                  <div class="building__hours-circle"></div>
                  <div class="building__hours-text">
                    Otevřeno do 19:00
                  </div>
                </div> -->
              </div>
            </nuxt-link>
          </div>
        </div>
      </div>

      <div v-if="page" class="mt-5 pages-container">
        <div v-for="(child, key) in page.children" :key="key">
          <PageTile :image="useGetImageUrl(child.image, 1000)" :title="child.name" :link="localePath(createLink(child))" />
        </div>
      </div>
    </div>
  </main>
</template>

<style lang="scss">
.building {
  $s: &;
  align-items: center;
  display: flex;
  height: 100%;
  padding: 16px 0;
  border-bottom: 1px solid $silver;
  padding: 20px 0;
  position: relative;

  &:last-child > & {
    border-bottom: none;
  }

  &:before,
  &:after {
    background: $black;
    content: '';
    height: 1px;
    position: absolute;
    right: 0;
    top: 50%;
    transform-origin: 100% 50%;
    width: 15px;
  }

  &:before {
    transform: rotate(45deg);
  }

  &:after {
    transform: rotate(-45deg);
  }

  &__image {
    display: inline-block;
    flex-shrink: 0;
    width: 128px;
    height: 128px;
    overflow: hidden;
  }

  &__image-elem {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.2s;
  }

  &__details {
    padding: 5px 15px;
  }

  &__name {
    margin-bottom: 5px;
  }

  &__description {
    font-weight: 400;
  }

  &__hours {
    display: flex;
    align-items: center;
  }

  &__hours-circle {
    width: 11px;
    height: 11px;
    border-radius: 9999px;
    background-color: $green;
    margin-right: 7px;
  }

  &:hover {
    #{$s}__image-elem {
      transform: scale(1.2);
    }
  }
}

.buildings {
  &__list {
    @include mq($lg) {
      display: flex;
      flex-wrap: wrap;

      .row > div {
        &:last-child {
          .building {
            border-bottom: 0;
          }
        }
      }
    }

    @include mq($xl) {
      .row > div {
        &:nth-last-of-type(-n + 2) {
          .building {
            border-bottom: 0;
          }
        }
      }
    }
  }

  .row {
    // ie11
    flex: 1;
  }
}

.pages-container {
  display: grid;
  grid-template-columns: 1fr;
  gap: 30px;
  margin: 0 auto;

  @media (min-width: 1200px) {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
